import { TRACKING_COOKIES } from "@repo/shared";

import { ApiClient } from "~/api/api-client";

/**
 * Consentimiento de medición: qué eligió la persona en el aviso de cookies.
 *
 * La fuente de verdad es la cookie `acd_consent`, que escribe la API (`POST /api/tracking/consent`)
 * y lee también el servidor para decidir si manda conversiones a las plataformas. El formato es
 * el de `apps/api/src/tracking/tracking-context.ts` (`a=1&m=0&v=2026-10&t=<epoch s>`), url-encoded
 * por Express. Las dos puntas tienen que leer lo mismo: por eso el parser acepta el valor crudo y
 * el codificado.
 */

export type ConsentCategories = {
  /** GA4, PostHog, Clarity. */
  analytics: boolean;
  /** Meta, TikTok, X, LinkedIn, Google Ads. */
  ads: boolean;
};

export type StoredConsent = ConsentCategories & {
  version: string | null;
  /** epoch en segundos. */
  at: number | null;
};

/** Dónde se dio: el aviso de cookies o «No medir en este navegador» de /privacy. */
export type ConsentSource = "banner" | "privacy";

const ONE_YEAR_S = 365 * 24 * 60 * 60;

export function parseConsentValue(raw: string | null | undefined): StoredConsent | null {
  if (!raw) return null;
  let value = raw.trim();
  try {
    if (value.includes("%")) value = decodeURIComponent(value);
  } catch {
    return null;
  }
  const params = new URLSearchParams(value);
  const a = params.get("a");
  const m = params.get("m");
  if ((a !== "0" && a !== "1") || (m !== "0" && m !== "1")) return null;
  const t = Number(params.get("t"));
  return {
    analytics: a === "1",
    ads: m === "1",
    version: params.get("v"),
    at: Number.isFinite(t) && t > 0 ? t : null,
  };
}

export function serializeConsentValue(choice: ConsentCategories, version: string, nowMs: number) {
  return new URLSearchParams({
    a: choice.analytics ? "1" : "0",
    m: choice.ads ? "1" : "0",
    v: version,
    t: String(Math.floor(nowMs / 1000)),
  }).toString();
}

export function readCookie(name: string, cookieString: string): string | null {
  for (const part of cookieString.split(";")) {
    const index = part.indexOf("=");
    if (index === -1) continue;
    if (part.slice(0, index).trim() === name) return part.slice(index + 1).trim();
  }
  return null;
}

export function readStoredConsent(): StoredConsent | null {
  if (typeof document === "undefined") return null;
  return parseConsentValue(readCookie(TRACKING_COOKIES.CONSENT, document.cookie));
}

/**
 * La elección vale si existe y es de la versión vigente del texto. Subir `CONSENT_VERSION` en la
 * API vuelve a preguntar.
 */
export function isChoiceCurrent(stored: StoredConsent | null, version: string | undefined) {
  if (!stored) return false;
  if (!version) return true;
  return stored.version === version;
}

/**
 * Lo que rige ahora:
 * 1. la elección vigente (versión actual) de la persona, si hay — también con el aviso apagado
 *    (la crea «No medir en este navegador» de /privacy);
 * 2. si no, con el aviso de cookies APAGADO (`banner = false`), se mide todo;
 * 3. si no, el default que configuró el admin (`TRACKING_CONSENT_DEFAULT`, `denied` si no se tocó).
 */
export function effectiveConsent(
  stored: StoredConsent | null,
  version: string | undefined,
  consentDefault: "granted" | "denied" | undefined,
  banner = true,
): ConsentCategories {
  if (stored && isChoiceCurrent(stored, version)) {
    return { analytics: stored.analytics, ads: stored.ads };
  }
  if (!banner) return { analytics: true, ads: true };
  const granted = consentDefault === "granted";
  return { analytics: granted, ads: granted };
}

function writeFallbackCookie(choice: ConsentCategories, version: string) {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${TRACKING_COOKIES.CONSENT}=${encodeURIComponent(
      serializeConsentValue(choice, version, Date.now()),
    )}` + `; Max-Age=${ONE_YEAR_S}; Path=/; SameSite=Lax${secure}`;
}

type Listener = (choice: ConsentCategories) => void;
const listeners = new Set<Listener>();

export function onConsentChange(listener: Listener) {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

/**
 * Guarda la elección: la API escribe la cookie y deja la fila de auditoría en `consent_records`.
 * Si la API no contesta, igual se escribe la cookie acá para no volver a preguntar en cada
 * página (sin fila de auditoría: se reintenta la próxima vez que la persona cambie algo).
 */
export async function saveConsent(
  choice: ConsentCategories,
  version: string,
  source: ConsentSource = "banner",
) {
  try {
    await ApiClient.api.trackingControllerUpdateTrackingConsent({ ...choice, source });
  } catch {
    // La cookie es lo que importa para el resto de la navegación.
  }
  if (!isChoiceCurrent(readStoredConsent(), version)) writeFallbackCookie(choice, version);
  listeners.forEach((listener) => listener(choice));
}

/** Evento para reabrir el panel desde el footer o desde /privacy (sólo con el aviso prendido). */
export const OPEN_CONSENT_EVENT = "academia:consent-open";

export function openConsentPreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_CONSENT_EVENT));
}
